import * as React from "react";
import { cn } from "@/lib/utils";

export interface NotFoundViewProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Optional message to display instead of default text
   */
  message?: string;
  /**
   * Background image opacity (0 to 1). Default: 0.12 for subtle effect
   */
  backgroundOpacity?: number;
}

/**
 * NotFoundView component - 404 error page view
 *
 * Displays the ANYW3AR logo with a friendly error message when users
 * reach a page that doesn't exist.
 *
 * @example
 * ```tsx
 * <NotFoundView />
 * <NotFoundView message="Custom error message" />
 * ```
 */
export const NotFoundView = React.forwardRef<HTMLDivElement, NotFoundViewProps>(
  ({ className, message, style, backgroundOpacity = 0.12, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col gap-8 items-start p-8 w-full box-border relative",
          className
        )}
        style={{
          backgroundColor: "#FFF",
          ...style,
        }}
        {...props}
      >
        {/* Background image layer with opacity */}
        <div
          className="absolute inset-0 bg-center bg-cover bg-no-repeat"
          style={{
            backgroundImage: "url(/images/404-background.jpg)",
            opacity: backgroundOpacity,
            zIndex: 0,
          }}
          aria-hidden="true"
        />

        {/* Header */}
        <div className="flex gap-4 items-center justify-center shrink-0 relative z-10">
          <p className="font-montserrat font-bold leading-normal text-[0.9375rem] text-gray-900 text-center uppercase">
            Error
          </p>
        </div>

        {/* Content - centered vertically with flex-1 */}
        <div className="flex flex-1 flex-col gap-[0.625rem] items-center justify-center min-h-0 min-w-0 shrink-0 w-full relative z-10">
          {/* ANYW3AR Logo */}
          <div className="relative shrink-0 w-[12.1044rem] h-[1.9806rem]">
            <img
              src="/anyw3ar-logo-404.svg"
              alt="ANYW3AR Logo"
              className="block max-w-none size-full"
            />
          </div>

          {/* Error Message - Line 1 */}
          <div className="flex flex-col font-montserrat font-normal justify-end leading-5 min-w-full text-[0.9375rem] text-gray-700 text-center w-min whitespace-pre-wrap">
            {message ? (
              <p className="mb-0">{message}</p>
            ) : (
              <>
                <p className="mb-0">Oups... </p>
                <p>It's seem that we arrived ANYW3AR</p>
              </>
            )}
          </div>

          {/* Error Message - Line 2 */}
          {!message && (
            <div className="flex flex-col font-montserrat font-normal justify-end leading-none min-w-full text-[0.9375rem] text-gray-700 text-center w-min">
              <p className="leading-5 whitespace-pre-wrap">
                Please, excuse us and try again...
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }
);

NotFoundView.displayName = "NotFoundView";
